import { forkJoin, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { HttpParamsObject } from '../services/api/api.service';

import { DataModel, ID } from './data.model';
import { DataStore } from './data.store';
import { ReducerManager, Store } from '@ngrx/store';

export interface DataService<Model extends DataModel> {
  dataName: string;
  // Store observables
  data$: Observable<Model[]>;
  lastSearchResultBundle;
  selectedData$: Observable<Model[]>;
  loading$: Observable<boolean>;
  error$: Observable<boolean>;

  /**
   * fetch, process and store data
   *
   * @param paramsObject search params
   * @param selectedIds? ids of instances to set them selected initially
   */
  fetchData(paramsObject?: HttpParamsObject, selectedIds?: ID[]): void;

  /**
   * resets data list
   */
  clearData(): void;

  /**
   * fetch, process and store data using options of previous fetch
   *
   */
  refreshData(): void;

  /**
   * fetch, process and store data related to Patient
   *
   * @param patientId
   */
  fetchDataByPatientId(patientId: string): void;

  /**
   * fetch, process and store data related to current user (as Patient)
   *
   * @param patientId
   */
  fetchMyData(): void;

  /**
   * fetch Items from the server, process and return observable array of Items.
   *
   * @param paramsObject
   * @returns
   */
  searchItems$(paramsObject?: HttpParamsObject): Observable<Model[]>;

  /**
   * search Item in store and if not found fetch it from server. Returns observable item.
   * @param id
   * @returns
   *
   */
  getItemById$(id: string): Observable<Model>;

  /**
   * add new item (process Model Instance, post Resource to Server and refresh data)
   *
   * @param item Model Instance that should be added
   * @param patientId Id of Patient that should be assigned to Model Instance
   */
  addItem(item: Model, patientId?: string): void;

  /**
   * add new item (process Model Instance, post Resource to Server and refresh data) to current user (as Patient)

   * @param item Model Instance that should be added
   */
  addItemToMe(item: Model): void;

  /**
   * update item (process Model Instance, update Resource on Server and refresh data)
   *
   * @param item Model Instance that should be updated
   */
  updateItem(item: Model): void;

  /**
   * delete item by id (delete Resource from Server and refresh data)
   *
   * @param id
   */
  deleteItemById(id: ID): void;

  /**
   * delete list of items (delete Resources from Server and refresh data)
   *
   * @param dataList array of Model Instances or Resources
   */
  deleteItemsList(dataList: {}[]): void;

  /**
   * mark item as selected
   *
   * @param item Model Instance to be selected
   */
  selectItem(item: Model | any): void;
  selectItemById(id: ID): void;

  /**
   * mark all items (in store) as selected
   */
  selectAll(): void;

  /**
   * mark item as deselected
   *
   * @param item Model Instance to be deselected
   */
  deselectItem(item: Model): void;
  deselectItemById(id: ID): void;

  /**
   * mark all items (in store) as deselected
   */
  deselectAll(): void;

  /**
   * toggle item's selection
   *
   * @param item Model Instance, which selection to be toggled
   */
  toggleItem(item: Model): void;
  toggleItemById(id: ID): void;

  /**
   * toggle selection of all items (in store), i.e.
   * if all items are selected then deselect all
   * otherwise select all
   */
  toggleAll(): void;
}

export abstract class DataService<Model extends DataModel>
  implements DataService<Model>
{
  // Store
  private readonly dataStore: DataStore<Model>;

  /**
   * @param dataName Unique Name of Data (for store name)
   */
  constructor(dataName: string, store: Store, manager: ReducerManager) {
    this.dataName = dataName;

    this.dataStore = new DataStore<Model>(this.dataName, store, manager);

    this.data$ = this.dataStore.selectData();
    this.selectedData$ = this.dataStore.selectActiveData();
    this.loading$ = this.dataStore.selectIsLoading();
    this.error$ = this.dataStore.selectError();
  }

  // Selections are implementiert using MultiActiveEntries
  selectItem(item: Model | any): void {
    this.dataStore.addActiveItemById(item?.id);
  }
  selectItemById(id: ID): void {
    this.dataStore.addActiveItemById(id);
  }

  selectAll(): void {
    this.getAllIds().subscribe((ids) => {
      this.dataStore.setActiveItems(ids);
    });
  }

  deselectItem(item: Model): void {
    this.dataStore.deselectItemById(item?.id);
  }
  deselectItemById(id: ID): void {
    this.dataStore.deselectItemById(id);
  }

  deselectAll(): void {
    this.dataStore.setActiveItems([]);
  }

  toggleItem(item: Model): void {
    this.dataStore.toggleItemById(item?.id);
  }
  toggleItemById(id: ID): void {
    this.dataStore.toggleItemById(id);
  }

  toggleAll(): void {
    forkJoin([this.getSelectedIds(), this.getAllIds()])
      .pipe(take(1))
      .subscribe(([selectedIds, ids]) => {
        if (selectedIds?.length > 0 && selectedIds?.length <= ids?.length) {
          this.deselectAll();
        } else {
          this.selectAll();
        }
      });
  }

  /**
   * get ids of items, marked as selected
   *
   * @returns array of ids of selected items
   */
  getSelectedIds(): Observable<ID[]> {
    return this.dataStore.getSelectedIds();
  }

  /**
   * get ids of all items in store
   *
   * @returns array of ids
   */
  protected getAllIds(): Observable<ID[]> {
    return this.dataStore
      .getAllItems()
      .pipe(map((items) => items.map(({ id }) => id) || []));
  }

  setItems(items: Model[]): void {
    this.dataStore.setItems(items);
  }

  setLoading(isLoading: boolean): void {
    this.dataStore.setIsLoading(isLoading);
  }

  setError(err): void {
    this.dataStore.setError(err);
  }

  removeById(id: ID): void {
    this.dataStore.removeItemById(id);
  }

  addStoreItem(item: Model): void {
    this.dataStore.addItem(item);
  }

  updateStoreItem(id: ID, item: Model): void {
    this.dataStore.updateItem(id, item);
  }

  hasEntity(id: ID): boolean {
    return this.dataStore.hasEntity(id);
  }

  getStoreItem(id: ID): Observable<Model> {
    return this.dataStore.selectEntity(id);
  }

  setActive(selectedIds: ID[]): void {
    this.dataStore.setActiveItems(selectedIds);
  }

  clearData(): void {
    this.setItems([]);
  }

  /**
   * generate subService to handle filtered data
   * @param filterFn filter function for items
   */
  generateFilteredDataService(filterFn: (model: Model) => boolean) {
    const data$ = this.data$.pipe(map((data) => data.filter(filterFn)));
    const selectedData$ = this.selectedData$.pipe(
      map((selectedData) => selectedData.filter(filterFn)),
    );
    const getAll = async () =>
      await this.dataStore
        .getAllItems()
        .pipe(map((items) => items.filter(filterFn) || []))
        .toPromise();
    const selectItem = (item: Model) =>
      filterFn(item) && this.selectItemById(item.id);
    const selectAll = async () => (await getAll()).forEach(selectItem);

    const deselectItem = (item: Model) =>
      filterFn(item) && this.deselectItem(item);
    const deselectAll = async () => (await getAll()).forEach(deselectItem);

    const toggleItem = (item: Model) => {
      return filterFn(item) && this.toggleItemById(item.id);
    };
    const getAllIds = async () => (await getAll())?.map(({ id }) => id) || [];
    const getSelectedIds = () =>
      forkJoin([this.dataStore.getSelectedIds(), getAllIds()]).pipe(
        take(1),
        map(([selectedIds, ids]) => {
          return selectedIds.filter((id: ID) => ids.includes(id));
        }),
      );

    const filteredDataService = {
      data$,
      selectedData$,
      selectItem,
      selectAll,
      deselectItem,
      deselectAll,
      toggleItem,
      getSelectedIds,
    };

    Object.setPrototypeOf(filteredDataService, this);

    return filteredDataService;
  }
}
